import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScrollText } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const SECTIONS = [
  {
    n: '1',
    t: 'Acceptance of terms',
    p: 'By accessing or using Around the World AI (the "Service"), you agree to be bound by these Terms of Service. If you do not agree to any part of these Terms, do not use the Service.',
  },
  {
    n: '2',
    t: 'Description of service',
    p: 'Around the World AI provides an AI-powered creation platform including a chat-based app builder, free lead-generation tools (Flight Itinerary, Holiday Planner, PDF Editor, Invoice Generator, QR Code Maker, Resume Builder), premium subscriptions, and related content. Features and pricing may change with reasonable notice.',
  },
  {
    n: '3',
    t: 'User accounts',
    p: 'You may create an account using Google Sign-In. You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. You must be at least 16 years old (or the age of digital consent in your jurisdiction) to use the Service.',
  },
  {
    n: '4',
    t: 'Acceptable use',
    p: 'You agree not to (a) generate content that is unlawful, harmful, defamatory, hateful, or infringes third-party rights, (b) reverse engineer or attempt to extract source code, (c) resell, sublicense, or share your account, (d) use the Service to build competing products, (e) probe, scan, or test the vulnerability of the Service without authorization.',
  },
  {
    n: '5',
    t: 'AI-generated content',
    p: 'The Service uses third-party AI models (Anthropic Claude, OpenAI, Google Gemini). Outputs are generated automatically and may contain inaccuracies, biases, or hallucinated facts. You are responsible for reviewing, editing, and validating all AI output before use. Around the World AI makes no warranty regarding accuracy, fitness for a particular purpose, or non-infringement of AI-generated content.',
  },
  {
    n: '6',
    t: 'Payment terms',
    p: 'Paid plans are billed monthly or annually via Stripe. All fees are non-refundable except where explicitly stated (e.g., our 14-day money-back guarantee on new subscriptions). Prices exclude applicable taxes which are your responsibility. Failed payments may result in service suspension until resolved.',
  },
  {
    n: '7',
    t: 'Credits and consumption',
    p: 'AI credits are consumed per generation. Unused credits reset each billing period unless otherwise stated in your plan. Credit balances are not refundable or transferable.',
  },
  {
    n: '8',
    t: 'Intellectual property',
    p: 'The Service, brand, code, and design are owned by Around the World AI. You retain ownership of prompts and content you submit, and of AI outputs generated for you (to the extent permitted by underlying AI providers). You grant us a limited license to process this content solely to provide the Service.',
  },
  {
    n: '9',
    t: 'Termination',
    p: 'You may cancel your subscription at any time from your account settings. We may suspend or terminate your access if you violate these Terms, engage in fraud, or if required by law. Upon termination, your right to use the Service ends immediately.',
  },
  {
    n: '10',
    t: 'Disclaimers',
    p: 'The Service is provided "AS IS" without warranties of any kind, express or implied. We do not guarantee uninterrupted availability, error-free operation, or that specific features will remain available.',
  },
  {
    n: '11',
    t: 'Limitation of liability',
    p: 'To the fullest extent permitted by law, Around the World AI, its officers, and employees are not liable for any indirect, incidental, special, or consequential damages arising from your use of the Service. Total liability for direct damages is limited to the amount you paid us in the 3 months preceding the claim.',
  },
  {
    n: '12',
    t: 'Governing law',
    p: 'These Terms are governed by the laws of the jurisdiction in which Around the World AI is registered, without regard to conflict-of-law principles. Disputes shall be resolved in the courts of that jurisdiction, unless mandatory consumer protection laws provide otherwise.',
  },
  {
    n: '13',
    t: 'Changes to terms',
    p: 'We may update these Terms from time to time. Material changes will be communicated via email or in-app notice at least 14 days before taking effect. Continued use after changes take effect constitutes acceptance.',
  },
  {
    n: '14',
    t: 'Contact',
    p: 'For questions about these Terms, contact us at hello@aroundtheworldai.com.',
  },
];

export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />

      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="terms-back-btn"
              className="text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <BrandLogo size={30} />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-4"
            data-testid="terms-badge"
          >
            <ScrollText size={12} /> Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">
            Terms of Service
          </h1>
          <p className="mt-3 text-white/50 text-sm">
            Last updated: {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="space-y-8" data-testid="terms-content">
          {SECTIONS.map((s) => (
            <section key={s.n} id={`section-${s.n}`}>
              <div className="flex items-start gap-3 mb-2">
                <span
                  className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center font-mono text-[12px] font-bold"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0,240,255,0.2) 0%, rgba(255,0,127,0.2) 100%)',
                    color: '#00F0FF',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {s.n}
                </span>
                <h2 className="text-lg md:text-xl font-display font-black text-white leading-tight pt-0.5">
                  {s.t}
                </h2>
              </div>
              <p className="text-[14.5px] text-white/70 leading-[1.75] pl-10">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-[13px] text-white/60 leading-relaxed">
          These Terms are provided as a template baseline and do not constitute legal advice.
          Please consult a lawyer to customize them for your jurisdiction and business specifics.
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
